/**
 * Mapování typ widgetu → React renderer. Pro VĚRNOU reprodukci původního dashboardu používá
 * původní komponenty (Value, SubscribedChart, Foscams+PartialWrapper, HidingWrapper). Resolvuje
 * {slug}. Neznámý typ → graceful placeholder. Device widgety viz deviceWidgets.tsx.
 */

import React from "react";
import type { WidgetNode } from "@globals/layouts";
import { getWidgetType } from "@globals/layouts";
import { PowerHealthWidget } from "@client/components/dashboard/widgets/PowerHealthWidget";
import { StatsWidget } from "@client/components/dashboard/widgets/StatsWidget";
import { BmsWidget, PipWidget, TracerWidget } from "@client/components/layout/deviceWidgets";
import { AutomationStatusWidget, GaugeWidget, WindGaugeWidget } from "@client/components/layout/extraWidgets";
import { SubscribedChart } from "@client/components/valueDisplays/SubscribedChart";
import Value, { type ValueTone } from "@client/components/valueDisplays/Value";
import { CamerasWidget, ChartWidget, HistoryWidget, Placeholder, WindSectionWidget } from "./layoutWidgetParts";

export type WidgetContext = { slug?: string; deviceType?: "pip" | "tracer" | "bms" };

const slugify = (s: unknown, slug?: string): string => (typeof s === "string" ? (slug ? s.replace(/\{slug\}/g, slug) : s) : "");
const slugifyAll = (v: unknown, slug?: string): string[] => (Array.isArray(v) ? v.map((t) => slugify(t, slug)).filter(Boolean) : []);
const numOpt = (x: unknown): number | undefined => {
  const n = typeof x === "number" ? x : typeof x === "string" && x !== "" ? Number(x) : NaN;
  return Number.isFinite(n) ? n : undefined;
};
const asVariant = (x: unknown): "lg" | "md" | "sm" | undefined => (x === "lg" || x === "md" || x === "sm" ? x : undefined);
const asTone = (x: unknown): ValueTone | undefined => (x && typeof x === "object" && !Array.isArray(x) ? (x as ValueTone) : undefined);

export const renderWidget = (node: WidgetNode, ctx: WidgetContext): React.ReactNode => {
  const cfg = node.config ?? {};
  const topicId = slugify(cfg.topicId, ctx.slug);
  switch (node.type) {
    case "value-card":
    case "computed-value":
      return topicId ? (
        <Value topicId={topicId} variant={asVariant(cfg.variant)} dot={cfg.dot !== false} accent={cfg.accent !== false} tone={asTone(cfg.tone)} />
      ) : (
        <Placeholder label="Vyber veličinu" />
      );
    case "big-number":
      return topicId ? <Value topicId={topicId} variant="lg" /> : <Placeholder label="Vyber veličinu" />;
    case "chart": {
      const topics = slugifyAll(cfg.topics, ctx.slug);
      return topics.length ? <ChartWidget topics={topics} boolean={!!cfg.boolean} /> : <Placeholder label="Vyber veličiny" />;
    }
    case "boolean-timeline":
      return topicId ? <SubscribedChart topicIds={[topicId]} boolean /> : <Placeholder label="Vyber veličinu" />;
    case "gauge":
      return topicId ? <GaugeWidget topicId={topicId} min={numOpt(cfg.min)} max={numOpt(cfg.max)} variant={asVariant(cfg.variant)} /> : <Placeholder label="Vyber veličinu" />;
    case "wind-gauge":
      return topicId ? <WindGaugeWidget topicId={topicId} /> : <Placeholder label="Vyber veličinu" />;
    case "automation-status":
      return <AutomationStatusWidget automationId={typeof cfg.automationId === "string" && cfg.automationId ? cfg.automationId : undefined} />;
    case "history":
      return topicId ? <HistoryWidget topicId={topicId} boolean={!!cfg.boolean} onlyVisibility={!!cfg.onlyVisibility} /> : <Placeholder label="Vyber veličinu" />;
    case "wind-section":
      return topicId ? <WindSectionWidget topicId={topicId} boolean={!!cfg.boolean} /> : <Placeholder label="Vyber veličinu" />;
    case "daily-stats":
      return topicId ? <StatsWidget topicId={topicId} boolean={!!cfg.boolean} hourly={false} /> : <Placeholder label="Vyber veličinu" />;
    case "hourly-stats":
      return topicId ? <StatsWidget topicId={topicId} boolean={!!cfg.boolean} hourly={true} /> : <Placeholder label="Vyber veličinu" />;
    case "multi-metric-grid": {
      const topics = slugifyAll(cfg.topics, ctx.slug);
      return (
        <div className="flex flex-row flex-wrap items-stretch justify-center">
          {topics.map((t) => (
            <Value key={t} topicId={t} variant={asVariant(cfg.variant) ?? "md"} />
          ))}
        </div>
      );
    }
    case "cameras":
      return <CamerasWidget />;
    case "power-health":
      return <PowerHealthWidget />;
    case "heading": {
      const lvl = String(cfg.level ?? "2");
      const cls = lvl === "1" ? "text-2xl font-bold" : lvl === "3" ? "text-base font-semibold" : "text-xl font-bold";
      return <div className={cls}>{String(cfg.text ?? "")}</div>;
    }
    case "divider":
      return (
        <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
          <div className="h-px flex-1 bg-border" />
          {cfg.label ? <span>{String(cfg.label)}</span> : null}
          <div className="h-px flex-1 bg-border" />
        </div>
      );
    case "markdown-note":
      return <div className="whitespace-pre-wrap rounded border border-border bg-card p-2 text-sm">{String(cfg.content ?? "")}</div>;
    case "link-button":
      return (
        <a href={String(cfg.url ?? "#")} target="_blank" rel="noreferrer" className="inline-flex items-center rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground">
          {String(cfg.label ?? "Odkaz")}
        </a>
      );
    case "iframe":
      return cfg.src ? <iframe src={String(cfg.src)} title={node.id} className="h-full w-full rounded border border-border" sandbox="allow-scripts allow-same-origin" /> : <Placeholder label="Zadej URL" />;
    default: {
      const dot = node.type.indexOf(".");
      if (dot > 0) {
        const dev = node.type.slice(0, dot);
        const part = node.type.slice(dot + 1);
        const slug = slugify(cfg.slug, ctx.slug) || ctx.slug || "";
        if (dev === "pip") return <PipWidget slug={slug} part={part} compact={!!cfg.compact} />;
        if (dev === "tracer") return <TracerWidget slug={slug} part={part} compact={!!cfg.compact} />;
        if (dev === "bms") return <BmsWidget slug={slug} part={part} />;
      }
      const def = getWidgetType(node.type);
      return <Placeholder label={def ? def.label : `Neznámý widget: ${node.type}`} />;
    }
  }
};
